// Response schemas for the freeze endpoints (plan §4: POST /freezes, GET /freezes/:id; T32).
// Unknown keys are dropped, except on the frozen task and evidence views, which keep the page
// body for the task and evidence screens to parse with their own schemas.
import { z } from 'zod';

// The label column is 64; team roles get Leads ids redacted, which can lengthen it (review F-R5).
const REDACTION_HEADROOM = 6;
const MAX_LABEL = 64 * REDACTION_HEADROOM;
const MAX_NAME = 200;

// 64 lowercase hex digits: matches "e3b0...b855"; rejects uppercase and 63 digits.
// https://regex101.com/?flavor=javascript&regex=%5E%5B0-9a-f%5D%7B64%7D%24&testString=e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855
const SHA256_HEX = /^[0-9a-f]{64}$/;

export const freezeRefSchema = z.object({
  id: z.number().int().positive(),
  label: z.string().min(1).max(MAX_LABEL),
  at: z.iso.datetime(),
});

export const freezeSummarySchema = freezeRefSchema.extend({
  by: z.object({ id: z.number().int().positive(), displayName: z.string().max(MAX_NAME) }),
  snapshotSha256: z.string().regex(SHA256_HEX),
});

export const freezeDetailSchema = freezeSummarySchema.extend({
  counts: z.object({
    task: z.number().int().min(0),
    evidence_line: z.number().int().min(0),
    safeguard: z.number().int().min(0),
  }),
  hashVerified: z.boolean(),
  /** An owner purge replaced a secret in the frozen items, so the hash cannot verify any more. */
  amendedByPurge: z.boolean().optional(),
});

export const freezeListSchema = z.object({
  items: z.array(freezeSummarySchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().min(0),
});

export const afterFreezeSchema = z.object({
  freeze: freezeRefSchema.nullable(),
  afterFreeze: z.boolean(),
});

export const frozenViewSchema = z.looseObject({
  freeze: freezeRefSchema,
  afterFreeze: z.boolean(),
});

export type FreezeRef = z.infer<typeof freezeRefSchema>;
export type FreezeSummary = z.infer<typeof freezeSummarySchema>;
export type FreezeDetail = z.infer<typeof freezeDetailSchema>;
export type FreezeList = z.infer<typeof freezeListSchema>;
export type AfterFreeze = z.infer<typeof afterFreezeSchema>;
export type FrozenView = z.infer<typeof frozenViewSchema>;
